<script lang="ts" setup>
import { computed, ref } from '#imports';
import type { VideoMsgVO } from '@core/types/im';
import { READ_STATUS } from '../../constant';
import ImMsgBase from './ImMsgBase.vue';

interface Props {
  msg: VideoMsgVO;
}

const { msg } = defineProps<Props>();
const imgLoadError = ref(false);

const emit = defineEmits(['retry', 'read', 'preview']);

function handleRetry() {
  emit('retry');
}
function handleRead() {
  emit('read', READ_STATUS.READ_AND_PLAY);
}

const maxWidth = 280;
const maxHeight = 168;
const renderHeight = ref(maxHeight);
const imgStyle = computed(() => ({
  height: renderHeight.value + 'px',
}));

function handlePreview() {
  emit('preview');
  handleRead();
}
function handleImgError() {
  imgLoadError.value = true;
}
function handleImgLoad(e: Event) {
  imgLoadError.value = false;
  const target = e.target as HTMLImageElement | null;
  const ow = target?.naturalWidth;
  const oh = target?.naturalHeight;
  calRenderHeight(ow, oh);
}
function calRenderHeight(ow?: number, oh?: number) {
  if (ow && oh) {
    renderHeight.value = Math.min(maxHeight, (oh * maxWidth) / ow);
  } else {
    renderHeight.value = maxHeight;
  }
}
</script>

<template>
  <ImMsgBase
    :recall="msg.msgBody.recall"
    :avatar="msg.avatar"
    :show-datetime="msg.showDatetime"
    :time="msg.msgTime"
    :placement="msg.placement"
    :loading="msg.loading"
    :error="msg.error"
    :unread="msg.readStatus === 0 && !msg.isMine"
    @retry="handleRetry"
  >
    <div class="cursor-pointer" @click="handlePreview">
      <div
        v-if="imgLoadError"
        class="bg-colorFill flex-center h-[168px] w-[280px]"
      >
        <img
          class="h-14 w-14"
          src="@core/assets/image/im/icon-video-error.svg"
          alt="error"
        />
      </div>
      <div v-else class="relative">
        <img
          :src="msg.msgBody.videoMsg.backgroundImage"
          alt="image"
          class="max-h-[168px] max-w-[280px] object-contain"
          :style="imgStyle"
          @error="handleImgError"
          @load="handleImgLoad"
        />
        <img
          class="pointer-events-none absolute left-1/2 top-1/2 -ml-4 -mt-4 h-8 w-8"
          src="@core/assets/image/im/icon-video-play.svg"
          alt="error"
        />
      </div>
    </div>
  </ImMsgBase>
</template>

<style scoped></style>
